import {
  isApiRequestError,
  getStateWithRevisionRequest,
  putStateWithRevisionRequest,
} from "./api";

type Listener = () => void;
type Updater<T> = T | ((prev: T) => T);

interface ServerStateStoreOptions {
  pollIntervalMs?: number;
}

const DEFAULT_POLL_INTERVAL_MS = 15000;
const MAX_SAVE_RETRIES = 3;

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}


function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasStableId(value: unknown): value is { id: string | number } {
  return isPlainRecord(value) && (typeof value.id === "string" || typeof value.id === "number");
}

function valuesEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function mergeConflictValue<T>(base: T, attempted: T, latest: T): T {
  if (Array.isArray(base) && Array.isArray(attempted) && Array.isArray(latest)) {
    const baseById = new Map(base.filter(hasStableId).map((item) => [String(item.id), item]));
    const latestById = new Map(latest.filter(hasStableId).map((item) => [String(item.id), item]));
    const attemptedIds = new Set(attempted.filter(hasStableId).map((item) => String(item.id)));
    const removedIds = new Set<string>();

    for (const item of base.filter(hasStableId)) {
      if (!attemptedIds.has(String(item.id))) {
        removedIds.add(String(item.id));
      }
    }

    for (const attemptedItem of attempted.filter(hasStableId)) {
      const id = String(attemptedItem.id);
      const baseItem = baseById.get(id);
      const latestItem = latestById.get(id);

      if (!baseItem) {
        latestById.set(id, attemptedItem);
        continue;
      }

      if (latestItem && !valuesEqual(baseItem, attemptedItem)) {
        latestById.set(id, mergeConflictValue(baseItem, attemptedItem, latestItem));
      }
    }

    const merged = latest
      .filter((item) => !hasStableId(item) || !removedIds.has(String(item.id)))
      .map((item) => {
        if (!hasStableId(item)) return item;
        return latestById.get(String(item.id)) ?? item;
      });

    for (const [id, item] of latestById.entries()) {
      if (!merged.some((candidate) => hasStableId(candidate) && String(candidate.id) === id)) {
        merged.unshift(item);
      }
    }

    return merged as T;
  }

  if (isPlainRecord(base) && isPlainRecord(attempted) && isPlainRecord(latest)) {
    const output: Record<string, unknown> = { ...latest };

    for (const key of Object.keys(attempted)) {
      const before = base[key];
      const wanted = attempted[key];
      const currentValue = latest[key];

      if (valuesEqual(before, wanted)) continue;

      if (
        typeof before === "number" &&
        typeof wanted === "number" &&
        typeof currentValue === "number"
      ) {
        output[key] = currentValue + (wanted - before);
      } else {
        output[key] = mergeConflictValue(before as never, wanted as never, currentValue as never);
      }
    }

    return output as T;
  }

  return attempted;
}

function isRevisionConflict(error: unknown) {
  return isApiRequestError(error) && error.status === 409;
}

export function createServerStateStore<T>(
  key: string,
  defaultValue: T,
  normalizer?: (value: unknown) => T,
  options?: ServerStateStoreOptions
) {
  let current: T = deepClone(defaultValue);
  let revision = 1;
  const listeners = new Set<Listener>();
  let pollTimer: number | null = null;
  let polling = false;
  let rehydrating = false;

  const pollIntervalMs = Math.max(
    5000,
    Number(options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS)
  );

  function emit() {
    listeners.forEach((listener) => listener());
  }

  function normalize(value: unknown): T {
    if (normalizer) {
      return normalizer(value);
    }

    if (value === undefined || value === null) {
      return deepClone(defaultValue);
    }

    return value as T;
  }

  async function rehydrate() {
    if (rehydrating) {
      return current;
    }

    rehydrating = true;

    try {
      const result = await getStateWithRevisionRequest<T>(key);
      current = normalize(result.data);
      revision = result.revision;
      emit();
      return current;
    } catch {
      return current;
    } finally {
      rehydrating = false;
    }
  }

  async function saveWithRetry(updater: Updater<T>) {
    let lastError: unknown = null;

    for (let attempt = 0; attempt < MAX_SAVE_RETRIES; attempt++) {
      const base = deepClone(current);
      const baseRevision = revision;
      const next =
        typeof updater === "function"
          ? (updater as (prev: T) => T)(deepClone(base))
          : updater;

      current = normalize(next);
      emit();

      try {
        const saved = await putStateWithRevisionRequest<T>(key, current, baseRevision);
        current = normalize(saved.data);
        revision = saved.revision;
        emit();
        return current;
      } catch (error) {
        lastError = error;

        if (!isRevisionConflict(error)) {
          current = base;
          revision = baseRevision;
          emit();
          throw error;
        }

        const attempted = deepClone(current);
        await rehydrate();
        current = normalize(mergeConflictValue(base, attempted, current));
        emit();

        try {
          const saved = await putStateWithRevisionRequest<T>(key, current, revision);
          current = normalize(saved.data);
          revision = saved.revision;
          emit();
          return current;
        } catch (retryError) {
          lastError = retryError;
          if (!isRevisionConflict(retryError)) {
            throw retryError;
          }
          await rehydrate();
        }
      }
    }

    throw lastError ?? new Error("Kayıt çakışması çözülemedi.");
  }

  async function setState(updater: Updater<T>) {
    await rehydrate();
    return saveWithRetry(updater);
  }

  async function reset() {
    await rehydrate();
    return saveWithRetry(deepClone(defaultValue));
  }

  function tick() {
    if (typeof window === "undefined") return;
    if (document.visibilityState === "hidden") return;
    if (!navigator.onLine) return;
    void rehydrate();
  }

  function ensurePolling() {
    if (polling || typeof window === "undefined") return;

    polling = true;
    pollTimer = window.setInterval(tick, pollIntervalMs);

    document.addEventListener("visibilitychange", tick);
    window.addEventListener("online", tick);
  }

  function stopPolling() {
    if (pollTimer !== null && typeof window !== "undefined") {
      window.clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("online", tick);
    }

    pollTimer = null;
    polling = false;
  }

  function subscribe(listener: Listener) {
    listeners.add(listener);

    if (listeners.size === 1) {
      ensurePolling();
      void rehydrate();
    }

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        stopPolling();
      }
    };
  }

  function getState() {
    return current;
  }

  return {
    getState,
    rehydrate,
    subscribe,
    setState,
    reset,
  };
}
